import { getTenantSlug } from '../utils/appOrigin';

// Dynamically derive base URL (fallback to current host:3001 in dev if env missing)
export const API_BASE_URL = (() => {
  // Prefer explicit env override if provided
  const injected = process.env.REACT_APP_API_BASE_URL;
  if (injected && typeof injected === 'string' && injected.trim()) {
    return injected.trim().replace(/\/$/, '');
  }
  try {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const host = origin ? new URL(origin).hostname : '';
    const port = origin ? new URL(origin).port : '';
    const isLocalHost = /^(localhost|127\.0\.0\.1|\[?::1\]?)$/i.test(host);
    // In local dev where frontend is running on 3000, backend defaults to 3001
    if (isLocalHost && port && port !== '3001' && port !== '5000' && port !== '5001') {
      return `http://${host}:3001/api`;
    }
    // In production or when served by backend directly
    return origin ? `${origin.replace(/\/$/, '')}/api` : '/api';
  } catch {
    return 'http://localhost:3001/api';
  }
})();

// Auth storage keys
const LS_JWT_TOKEN_KEY = 'testara_auth_token';
const LS_API_KEY_ACTIVE = 'testara_active_api_key_full';
const LS_API_KEY_ACTIVE_ID = 'testara_active_api_key_id';
const LS_AUTH_MODE = 'testara_auth_mode'; // 'jwt' | 'apikey'

const OFFLINE_QUEUE_KEY = 'testara_offline_ops';

// Gate debug logs in non-production
const __DEV__ = typeof process !== 'undefined' ? process.env.NODE_ENV !== 'production' : true;
const devLog = {
  log: (...args) => { if (__DEV__) { try { console.log(...args); } catch {} } },
  warn: (...args) => { if (__DEV__) { try { console.warn(...args); } catch {} } },
};

// Build Authorization headers based on stored auth mode (JWT or API Key)
function buildAuthHeaders() {
  const headers = {};
  try {
    const mode = localStorage.getItem(LS_AUTH_MODE) || 'jwt';
    if (mode === 'apikey') {
      const apiKey = localStorage.getItem(LS_API_KEY_ACTIVE);
      if (apiKey) {
        headers.Authorization = `Bearer ${apiKey}`;
      } else {
        const token = localStorage.getItem(LS_JWT_TOKEN_KEY);
        if (token) headers.Authorization = `Bearer ${token}`;
      }
    } else {
      const token = localStorage.getItem(LS_JWT_TOKEN_KEY);
      if (token) headers.Authorization = `Bearer ${token}`;
    }
  } catch {}
  return headers;
}

// Helper available before class definition to respect runtime override
function getEffectiveBaseUrlLocal() {
  try {
    const o = localStorage.getItem('testara_api_base_override');
    if (o && o.trim()) return o.trim().replace(/\/$/, '');
  } catch {}
  return API_BASE_URL;
}

function loadOfflineQueue() {
  try {
    return JSON.parse(localStorage.getItem(OFFLINE_QUEUE_KEY) || '[]');
  } catch {
    return [];
  }
}

function saveOfflineQueue(queue) {
  try {
    localStorage.setItem(OFFLINE_QUEUE_KEY, JSON.stringify(queue));
  } catch {
    // ignore quota/storage errors in offline queue
  }
}

async function isBackendReachable() {
  const controller = new AbortController();
  const t = setTimeout(() => controller.abort(), 2000);
  try {
    const res = await fetch(`${getEffectiveBaseUrlLocal()}/health`, { signal: controller.signal });
    clearTimeout(t);
    return res.ok;
  } catch {
    clearTimeout(t);
    return false;
  }
}

function hasAuthHeader(headers) {
  return !!headers && typeof headers.Authorization === 'string' && headers.Authorization.trim().length > 0;
}

async function isAuthorizedReachable() {
  // Use an auth-protected endpoint to verify both connectivity and authorization
  const headers = buildAuthHeaders();
  if (!hasAuthHeader(headers)) {
    return { ok: false, status: 0, reason: 'unauthenticated' };
  }
  const controller = new AbortController();
  const t = setTimeout(() => controller.abort(), 4000);
  try {
    const res = await fetch(`${getEffectiveBaseUrlLocal()}/projects`, { headers, signal: controller.signal });
    clearTimeout(t);
    if (res.ok) return { ok: true, status: res.status };
    // propagate useful statuses
    return { ok: false, status: res.status, reason: res.status === 401 ? 'unauthorized' : (res.status === 402 ? 'payment_required' : (res.status === 403 ? 'forbidden' : 'error')) };
  } catch (e) {
    clearTimeout(t);
    return { ok: false, status: 'ERR', reason: e.name === 'AbortError' ? 'timeout' : 'network' };
  }
}

// Attempt to flush queued offline archive/unarchive operations
async function flushOfflineQueue() {
  const queue = loadOfflineQueue();
  if (queue.length === 0) return { total: 0, succeeded: 0, failed: 0, remaining: 0 };

  // First, require backend to be up
  const reachable = await isBackendReachable();
  if (!reachable) return { total: queue.length, succeeded: 0, failed: 0, remaining: queue.length, blocked: true, blockedStatus: 0, blockedReason: 'offline' };

  // Next, require authorized access (JWT or API key + valid + subscription where applicable)
  const authReach = await isAuthorizedReachable();
  if (!authReach.ok) {
    return { total: queue.length, succeeded: 0, failed: 0, remaining: queue.length, blocked: true, blockedStatus: authReach.status, blockedReason: authReach.reason || 'unauthorized' };
  }

  devLog.log('[OfflineSync] Flushing queued operations:', queue.length);
  const remaining = [];
  let succeeded = 0;
  const failures = [];
  for (const op of queue) {
    try {
      let endpoint, method = 'PATCH', body;
      if (op.action === 'archive') {
        endpoint = `/testruns/${op.id}/archive`;
      } else if (op.action === 'unarchive') {
        endpoint = `/testruns/${op.id}/unarchive`;
      } else if (op.action === 'updateTestRun') {
        endpoint = `/testruns/${op.id}`;
        method = 'PUT';
        body = JSON.stringify(op.data);
      } else if (op.action === 'createTestRun') {
        endpoint = `/testruns`;
        method = 'POST';
        body = JSON.stringify(op.data);
      } else {
        continue;
      }
      const resp = await fetch(`${getEffectiveBaseUrlLocal()}${endpoint}`, {
        method,
        headers: { 'Content-Type': 'application/json', ...buildAuthHeaders() },
        body
      });
      if (!resp.ok) {
        failures.push({ id: op.id, action: op.action, status: resp.status });
        throw new Error(`HTTP ${resp.status}`);
      }
      succeeded += 1;
      devLog.log(`[OfflineSync] ${op.action} success for ${op.id}`);
    } catch (e) {
      devLog.warn(`[OfflineSync] ${op.action} failed for ${op.id}, keeping in queue`, e.message);
      remaining.push(op);
    }
  }
  saveOfflineQueue(remaining);
  return { total: queue.length, succeeded, failed: failures.length, remaining: remaining.length, failures };
}

// Strip client-only / bloated fields before PUT to avoid oversized payloads and cast errors.
export function stripTestRunForPut(testRun) {
  if (!testRun || typeof testRun !== 'object') return testRun;
  const coerceId = (v) => {
    if (!v) return v;
    if (typeof v === 'string') return v;
    if (typeof v === 'object' && v._id) return v._id;
    return v;
  };
  const mapStep = (sr) => ({
    index: sr.index,
    status: sr.status,
    notes: sr.notes || '',
    startedAt: sr.startedAt,
    endedAt: sr.endedAt,
    durationMs: sr.durationMs,
    attachments: Array.isArray(sr.attachments) ? sr.attachments : [],
  });
  const mapCase = (r) => ({
    testCaseId: coerceId(r.testCaseId),
    status: r.status,
    executedDate: r.executedDate,
    notes: r.notes || '',
    stepResults: Array.isArray(r.stepResults) ? r.stepResults.map(mapStep) : undefined,
    startedAt: r.startedAt,
    endedAt: r.endedAt,
    totalDurationMs: r.totalDurationMs,
    assignedTo: coerceId(r.assignedTo),
    assignedToEmail: r.assignedToEmail,
    assignedAt: r.assignedAt,
    orderIndex: r.orderIndex,
  });
  const payload = {
    name: testRun.name,
    description: testRun.description,
    projectId: coerceId(testRun.projectId),
    testPlanId: coerceId(testRun.testPlanId),
    executedBy: testRun.executedBy,
    startDate: testRun.startDate,
    endDate: testRun.endDate,
    status: testRun.status,
    totalTests: testRun.totalTests,
    passedTests: testRun.passedTests,
    failedTests: testRun.failedTests,
    skippedTests: testRun.skippedTests,
  };
  if (Array.isArray(testRun.labels)) {
    payload.labels = testRun.labels
      .map((l) => coerceId(typeof l === 'object' && l ? (l._id || l.id) : l))
      .filter(Boolean);
  }
  if (Array.isArray(testRun.testCaseResults) && testRun.testCaseResults.length > 0) {
    payload.testCaseResults = testRun.testCaseResults.map(mapCase);
  }
  if (testRun.environmentId) payload.environmentId = coerceId(testRun.environmentId);
  return payload;
}

/** Metadata-only PUT — never sends testCaseResults (safe for slim-list edits). */
export function stripTestRunForMetadataPut(testRun) {
  const payload = stripTestRunForPut(testRun);
  delete payload.testCaseResults;
  return payload;
}

export function computeAssignmentSummary(testCaseResults) {
  const rows = Array.isArray(testCaseResults) ? testCaseResults : [];
  let assigned = 0;
  for (const row of rows) {
    if (row?.assignedTo) assigned += 1;
  }
  return { assigned, unassigned: Math.max(0, rows.length - assigned) };
}

/** Merge API patches into list state without dropping nested execution data. */
export function mergeTestRunState(existing, patch, { stripResults = false } = {}) {
  if (!patch) return existing;
  if (!existing) return patch;
  const merged = { ...existing, ...patch };
  if (stripResults) {
    delete merged.testCaseResults;
  } else if (!Array.isArray(patch.testCaseResults)) {
    if (existing.testCaseResults) merged.testCaseResults = existing.testCaseResults;
  } else if (patch.testCaseResults.length === 0 && existing.testCaseResults?.length) {
    merged.testCaseResults = existing.testCaseResults;
  }
  if (Array.isArray(merged.testCaseResults) && merged.testCaseResults.length) {
    merged.assignmentSummary = computeAssignmentSummary(merged.testCaseResults);
  }
  return merged;
}

export function slimTestRunForCache(run) {
  if (!run || typeof run !== 'object') return run;
  const { testCaseResults, ...rest } = run;
  return rest;
}

export function testCaseResultToPatchRow(result, orderIndex = 0) {
  const coerceId = (v) => {
    if (!v) return v;
    if (typeof v === 'string') return v;
    if (typeof v === 'object' && v._id) return v._id;
    return v;
  };
  return {
    testCaseId: coerceId(result.testCaseId),
    status: result.status,
    executedDate: result.executedDate,
    notes: result.notes || '',
    orderIndex,
    startedAt: result.startedAt,
    endedAt: result.endedAt,
    totalDurationMs: result.totalDurationMs,
    stepResults: Array.isArray(result.stepResults)
      ? result.stepResults.map((sr) => ({
        index: sr.index,
        status: sr.status,
        notes: sr.notes || '',
        startedAt: sr.startedAt,
        endedAt: sr.endedAt,
        durationMs: sr.durationMs,
        attachments: Array.isArray(sr.attachments) ? sr.attachments : [],
      }))
      : undefined,
  };
}

class ApiService {
  static _auditLogsCache = { ts: 0, paramsKey: '', data: null };

  // Short-lived GET dedupe: shares in-flight promises and caches results briefly
  // so the startup burst (multiple effects requesting /users/me, /tasks, etc.)
  // results in a single network request per endpoint.
  static _getMemo = new Map();

  static memoGet(endpoint, ttlMs = 2000) {
    const now = Date.now();
    const entry = this._getMemo.get(endpoint);
    if (entry) {
      if (entry.promise) return entry.promise;
      if (now - entry.ts < ttlMs) return Promise.resolve(entry.data);
    }
    const promise = this.makeRequest(endpoint)
      .then((data) => {
        this._getMemo.set(endpoint, { ts: Date.now(), data });
        return data;
      })
      .catch((err) => {
        this._getMemo.delete(endpoint);
        throw err;
      });
    this._getMemo.set(endpoint, { promise });
    return promise;
  }

  static invalidateMemo(prefix) {
    for (const key of this._getMemo.keys()) {
      if (key.startsWith(prefix)) this._getMemo.delete(key);
    }
  }

  static getToken() {
    try {
      const mode = localStorage.getItem('testara_auth_mode') || 'jwt';
      if (mode === 'apikey') {
        const k = localStorage.getItem('testara_active_api_key_full');
        if (k) return k;
      }
      return localStorage.getItem('testara_auth_token') || sessionStorage.getItem('testara_auth_token') || null;
    } catch { return null; }
  }

  static getEffectiveBaseUrl() {
    try {
      const override = localStorage.getItem('testara_api_base_override');
      if (override && override.trim()) return override.trim().replace(/\/$/, '');
    } catch { /* ignore */ }
    return API_BASE_URL.replace(/\/$/, '');
  }

  static setBaseUrlOverride(url) {
    try {
      if (url && url.trim()) {
        localStorage.setItem('testara_api_base_override', url.trim());
      } else {
        localStorage.removeItem('testara_api_base_override');
      }
    } catch {}
  }
  static clearBaseUrlOverride() { this.setBaseUrlOverride(''); }
  static getBaseUrlOverride() {
    try { return localStorage.getItem('testara_api_base_override') || ''; } catch { return ''; }
  }

  // Expose offline queue for UI
  static getOfflineQueue() { return loadOfflineQueue(); }
  static async flushOfflineQueue() { return flushOfflineQueue(); }
  static async isAuthorizedReachable() { return isAuthorizedReachable(); }

  static setToken(tok) { try { tok ? localStorage.setItem(LS_JWT_TOKEN_KEY, tok) : localStorage.removeItem(LS_JWT_TOKEN_KEY); } catch {} }
  static clearToken() { this.setToken(''); }

  static setActiveApiKey(fullKey, keyId) {
    try {
      if (fullKey) localStorage.setItem(LS_API_KEY_ACTIVE, fullKey); else localStorage.removeItem(LS_API_KEY_ACTIVE);
      if (keyId) localStorage.setItem(LS_API_KEY_ACTIVE_ID, keyId); else localStorage.removeItem(LS_API_KEY_ACTIVE_ID);
    } catch {}
  }
  static getActiveApiKey() { try { return localStorage.getItem(LS_API_KEY_ACTIVE) || ''; } catch { return ''; } }
  static getActiveApiKeyId() { try { return localStorage.getItem(LS_API_KEY_ACTIVE_ID) || ''; } catch { return ''; } }
  static clearActiveApiKey() { this.setActiveApiKey('', ''); }

  static getActiveAuthMode() { try { return localStorage.getItem(LS_AUTH_MODE) || 'jwt'; } catch { return 'jwt'; } }
  static setActiveAuthMode(mode) { try { localStorage.setItem(LS_AUTH_MODE, mode); } catch {} }

  static async makeRequest(endpoint, options = {}) {
    try {
      const { suppressPlanUpgrade = false, ...fetchOptions } = options || {};
      const headers = { 'Content-Type': 'application/json', ...fetchOptions.headers };
      const authMode = this.getActiveAuthMode();
      if (authMode === 'apikey') {
        const apiKey = this.getActiveApiKey();
        if (apiKey) {
          headers.Authorization = `Bearer ${apiKey}`;
        } else {
          const token = this.getToken();
          if (token) headers.Authorization = `Bearer ${token}`;
        }
      } else {
        const token = this.getToken();
        if (token) headers.Authorization = `Bearer ${token}`;
      }
      const response = await fetch(`${this.getEffectiveBaseUrl()}${endpoint}`, {
        ...fetchOptions,
        headers
      });

      const rawText = await response.text().catch(() => '');
      if (!response.ok) {
        let msg = '';
        let json = null;
        try { json = rawText ? JSON.parse(rawText) : null; } catch { json = null; }
        msg = json?.message || rawText || `HTTP error! status: ${response.status}`;
        const err = new Error(msg);
        err.status = response.status;
        err.raw = rawText;
        if (json?.code) err.code = json.code; // propagate server-side error code (e.g. 'email_not_verified')
        if (json?.provider) err.provider = json.provider;
        if (json?.requiredPlan) err.requiredPlan = json.requiredPlan;
        if (json?.currentPlan) err.currentPlan = json.currentPlan;

        // If Testera session auth is expired/invalid, clear local auth to avoid the app
        // getting stuck in an "authenticated" UI state while every request returns 401.
        // IMPORTANT:
        // - only wipe when we actually sent credentials
        // - never wipe on upstream provider 401s (GitLab/GitHub/Bitbucket MCP token expiry
        //   used to clear the JWT and break MCP OAuth Connect with "Authentication required")
        const isTesteraSession401 = err.status === 401
          && hasAuthHeader(headers)
          && !json?.provider
          && (
            /^Authentication required$/i.test(String(msg || '').trim())
            || json?.code === 'mfa_session_invalid'
            || /invalid or expired 2FA session/i.test(String(msg || ''))
          );
        if (isTesteraSession401) {
          try {
            this.clearToken?.();
            this.clearActiveApiKey?.();
            localStorage.removeItem('testara_currentUser');
            localStorage.removeItem('testara_auth_mode');
          } catch {}
          try {
            window.dispatchEvent(new CustomEvent('auth-required', { detail: { reason: 'unauthorized' } }));
          } catch {}
        }

        // Signal free plan limit
        if (err.status === 403 && /Free plan limit reached/i.test(msg)) {
          try {
            let entity = null, limit = null;
            const m = msg.match(/maximum\s+(\d+)\s+([a-zA-Z]+)/i);
            if (m) { limit = parseInt(m[1], 10); entity = m[2]; }
            window.dispatchEvent(new CustomEvent('free-plan-limit', { detail: { message: msg, entity, limit } }));
          } catch (_) {}
        }

        // Public demo: writes are read-only — show one friendly sign-up nudge
        if (err.status === 403 && json?.code === 'DEMO_READ_ONLY') {
          try {
            window.dispatchEvent(new CustomEvent('demo-read-only', { detail: json }));
          } catch (_) {}
        }

        // Signal AI credit exhaustion — any AI surface can trigger the buy-credits modal
        if (err.status === 402 && json?.code === 'AI_CREDITS_EXHAUSTED') {
          try {
            window.dispatchEvent(new CustomEvent('ai-credits-exhausted', { detail: json }));
          } catch (_) {}
        }

        // Signal plan upgrade required (Allure, compare, Companion, case limits, etc.)
        // Skip for browse/explore GETs that pass suppressPlanUpgrade — upgrade on intentional use instead.
        if (
          !suppressPlanUpgrade
          && err.status === 403
          && (json?.code === 'PLAN_UPGRADE_REQUIRED' || json?.code === 'PLAN_CASE_LIMIT')
        ) {
          try {
            window.dispatchEvent(new CustomEvent('plan-upgrade-required', { detail: json }));
          } catch (_) {}
        }
        throw err;
      }

      if (!rawText) return {};
      try { return JSON.parse(rawText); } catch { return {}; }
    } catch (error) {
      if (error.name === 'AbortError') {
        error.message = 'Request timed out';
        error.status = error.status || 0;
      } else if (/504|Gateway Time-out|Gateway Timeout/i.test(error.message || '') || error.status === 504) {
        if (!error.message || /^(?:504|Gateway Time-?out)$/i.test(String(error.message).trim()) || /<html|<!doctype/i.test(error.message)) {
          error.message = 'Gateway timeout from upstream';
        }
        error.status = 504;
      } else if (error instanceof TypeError) {
        error.message = 'Network error or server unreachable';
        error.status = error.status || 0;
      }
      console.error('API request failed:', error.message);
      throw error;
    }
  }

  // Projects
  static async getProjects() {
    return this.makeRequest('/projects');
  }

  static async createProject(projectData) {
    return this.makeRequest('/projects', {
      method: 'POST',
      body: JSON.stringify(projectData),
    });
  }

  static async updateProject(id, projectData) {
    return this.makeRequest(`/projects/${id}`, {
      method: 'PUT',
      body: JSON.stringify(projectData),
    });
  }

  static async deleteProject(id) {
    return this.makeRequest(`/projects/${id}`, {
      method: 'DELETE',
    });
  }

  // Test Cases
  static async getTestCases() {
    return this.makeRequest('/testcases');
  }

  // NEW: optional filtered fetch (e.g., { assignedTo: 'me' | 'unassigned' | userId })
  static async getTestCasesFiltered(params = {}) {
    const qp = new URLSearchParams();
    Object.entries(params).forEach(([k,v]) => { if (v !== undefined && v !== null && v !== '') qp.set(k, v); });
    const qs = qp.toString();
    return this.makeRequest(`/testcases${qs ? ('?' + qs) : ''}`);
  }

  static async createTestCase(testCaseData) {
    return this.makeRequest('/testcases', {
      method: 'POST',
      body: JSON.stringify(testCaseData),
    });
  }

  static async updateTestCase(id, testCaseData) {
    return this.makeRequest(`/testcases/${id}`, {
      method: 'PUT',
      body: JSON.stringify(testCaseData),
    });
  }

  static async deleteTestCase(id) {
    return this.makeRequest(`/testcases/${id}`, {
      method: 'DELETE',
    });
  }

  // Scenarios — narrative parents linking 1..N test cases.
  static async getScenarios(params = {}) {
    const qp = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') qp.set(k, v); });
    const qs = qp.toString();
    return this.makeRequest(`/scenarios${qs ? `?${qs}` : ''}`);
  }

  /** Derived pass/fail per scenario for the selected run, environment, or plan. */
  static async getScenarioRollup(params = {}) {
    const qp = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') qp.set(k, v); });
    const qs = qp.toString();
    return this.makeRequest(`/scenarios/rollup${qs ? `?${qs}` : ''}`);
  }

  static async createScenario(data) {
    return this.makeRequest('/scenarios', { method: 'POST', body: JSON.stringify(data) });
  }

  static async updateScenario(id, data) {
    return this.makeRequest(`/scenarios/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  static async deleteScenario(id) {
    return this.makeRequest(`/scenarios/${id}`, { method: 'DELETE' });
  }

  /** Idempotent on scenario code within the target plan/project. */
  static async importScenarios({ rows, planId, projectId } = {}) {
    return this.makeRequest('/scenarios/import', {
      method: 'POST',
      body: JSON.stringify({ rows, planId, projectId }),
    });
  }

  // NEW: assignment endpoint helper (pass { assignedTo } or { assignedToEmail }, or { assignedTo: null } to unassign)
  static async assignTestCase(id, payload = {}) {
    const body = {};
    if (Object.prototype.hasOwnProperty.call(payload, 'assignedTo')) body.assignedTo = payload.assignedTo; // can be null
    if (payload.assignedToEmail) body.assignedToEmail = payload.assignedToEmail;
    const res = await this.makeRequest(`/testcases/${id}/assign`, {
      method: 'PATCH',
      body: JSON.stringify(body)
    });
    return res?.testCase || res;
  }

  // Test Plans
  static async getTestPlans() {
    return this.makeRequest('/testplans');
  }

  static async getTestPlan(id) {
    return this.makeRequest(`/testplans/${id}`);
  }

  static async createTestPlan(testPlanData) {
    return this.makeRequest('/testplans', {
      method: 'POST',
      body: JSON.stringify(testPlanData),
    });
  }

  static async updateTestPlan(id, testPlanData) {
    return this.makeRequest(`/testplans/${id}`, {
      method: 'PUT',
      body: JSON.stringify(testPlanData),
    });
  }

  static async deleteTestPlan(id) {
    return this.makeRequest(`/testplans/${id}`, {
      method: 'DELETE',
    });
  }

  /** Start automated CI run (GitHub Actions) for a plan with attached workflow. */
  static async startPlanCiRun(planId, body = {}) {
    return this.makeRequest(`/testplans/${planId}/ci-run`, {
      method: 'POST',
      body: JSON.stringify(body || {}),
    });
  }

  /** Start CI for a single test case (delegates to a CI-enabled plan). */
  static async startCaseCiRun(caseId, body = {}) {
    return this.makeRequest(`/testcases/${caseId}/ci-run`, {
      method: 'POST',
      body: JSON.stringify(body || {}),
    });
  }

  /** Push a test case to Testera Probe (derives request steps, upserts the chain). */
  static async pushCaseToProbe(caseId, body = {}) {
    return this.makeRequest(`/testcases/${caseId}/probe-push`, {
      method: 'POST',
      body: JSON.stringify(body || {}),
    });
  }

  /** Push a scenario (linked cases in order) to Testera Probe as one chain. */
  static async pushScenarioToProbe(scenarioId, body = {}) {
    return this.makeRequest(`/scenarios/${scenarioId}/probe-push`, {
      method: 'POST',
      body: JSON.stringify(body || {}),
    });
  }

  /** SSO handoff URL into Testera Probe, landing on `returnTo` (e.g. a chain's auto-run path). */
  static async getProbeSsoUrl(returnTo = '') {
    const qs = returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : '';
    return this.makeRequest(`/probe/sso${qs}`, { method: 'GET' });
  }

  /** Start CI for a scenario (all linked cases on a CI-enabled plan). */
  static async startScenarioCiRun(scenarioId, body = {}) {
    return this.makeRequest(`/scenarios/${scenarioId}/ci-run`, {
      method: 'POST',
      body: JSON.stringify(body || {}),
    });
  }

  /** Push live runner events (also used by CI suites via API key). */
  static async postRunnerEvents(runId, events) {
    return this.makeRequest(`/testruns/${runId}/runner-events`, {
      method: 'POST',
      body: JSON.stringify({ events }),
    });
  }

  /** SSE URL for live runner events (EventSource needs token query). */
  static runnerEventsStreamUrl(runId, { after } = {}) {
    const base = this.getEffectiveBaseUrl();
    const token = this.getToken();
    const qp = new URLSearchParams();
    if (token) qp.set('token', token);
    if (after != null && Number.isFinite(Number(after))) qp.set('after', String(after));
    return `${base}/testruns/${encodeURIComponent(runId)}/events/stream?${qp.toString()}`;
  }

  /** Inspect attached GitHub workflow for extra dispatch inputs / secrets. */
  static async getPlanCiParams(planId, params = {}) {
    const qp = new URLSearchParams();
    if (params.path) qp.set('path', params.path);
    if (params.ref) qp.set('ref', params.ref);
    const qs = qp.toString();
    return this.makeRequest(`/testplans/${encodeURIComponent(planId)}/ci-params${qs ? `?${qs}` : ''}`);
  }

  static async getGithubWorkflowParams(path, ref = 'main') {
    const qp = new URLSearchParams();
    if (path) qp.set('path', path);
    if (ref) qp.set('ref', ref);
    return this.makeRequest(`/github/workflow-params?${qp.toString()}`);
  }

  /** Create/update a thin GitHub Actions workflow for a plan and attach it. */
  static async createPlanCiWorkflow(planId, body = {}) {
    return this.makeRequest(`/testplans/${planId}/ci-workflow`, {
      method: 'POST',
      body: JSON.stringify(body || {}),
    });
  }

  /** Poll GitHub Actions status for a CI-backed test run. */
  static async syncTestRunCi(runId) {
    return this.makeRequest(`/testruns/${runId}/sync-ci`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  // Test Runs
  static async getTestRuns(params = {}) {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value != null && value !== '') qs.set(key, String(value));
    });
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return this.makeRequest(`/testruns${suffix}`);
  }

  static async createTestRun(testRunData) {
    try {
      return await this.makeRequest('/testruns', {
        method: 'POST',
        body: JSON.stringify(testRunData),
      });
    } catch (e) {
      if (e instanceof TypeError) {
        const offlineId = testRunData._id || `offline_tr_${Date.now()}`;
        const queue = loadOfflineQueue();
        queue.push({ id: offlineId, action: 'createTestRun', data: { ...testRunData, _id: undefined }, ts: Date.now() });
        saveOfflineQueue(queue);
        devLog.warn('[CreateTestRun] Queued offline creation', offlineId);
        return {
          _id: offlineId,
          ...testRunData,
          offlinePending: true
        };
      }
      throw e;
    }
  }

  static async updateTestRun(id, testRunData) {
    const payload = stripTestRunForPut(testRunData);
    try {
      return await this.makeRequest(`/testruns/${id}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
    } catch (e) {
      if (e instanceof TypeError) {
        const queue = loadOfflineQueue();
        queue.push({ id, action: 'updateTestRun', data: payload, ts: Date.now() });
        saveOfflineQueue(queue);
        devLog.warn('[UpdateTestRun] Queued offline update for', id);
        return {
          _id: id,
          ...payload,
          offlinePending: true
        };
      }
      throw e;
    }
  }

  static async getFailureAnalytics(params = {}) {
    const qp = new URLSearchParams();
    Object.entries(params || {}).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qp.set(k, v);
    });
    const qs = qp.toString();
    return this.makeRequest(`/analytics/failures${qs ? `?${qs}` : ''}`, { suppressPlanUpgrade: true });
  }

  /** Per-test-case execution analytics (coverage, never executed, failing, flaky). */
  static async getCaseAnalytics(params = {}) {
    const qp = new URLSearchParams();
    Object.entries(params || {}).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '' && v !== 'all') qp.set(k, v);
    });
    const qs = qp.toString();
    return this.makeRequest(`/analytics/cases${qs ? `?${qs}` : ''}`, { suppressPlanUpgrade: true });
  }

  // PRODUCTION MONITORING (Marketplace app)
  static async getMonitoringOverview() {
    return this.makeRequest('/monitoring/overview', { suppressPlanUpgrade: true });
  }

  static async getMonitoringConfig() {
    return this.makeRequest('/monitoring/config', { suppressPlanUpgrade: true });
  }

  static async updateMonitoringConfig(config) {
    return this.makeRequest('/monitoring/config', { method: 'PUT', body: JSON.stringify({ config: config || {} }) });
  }

  static async createMonitor(data) {
    return this.makeRequest('/monitoring/monitors', { method: 'POST', body: JSON.stringify(data || {}) });
  }

  static async updateMonitor(id, data) {
    return this.makeRequest(`/monitoring/monitors/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(data || {}) });
  }

  static async deleteMonitor(id) {
    return this.makeRequest(`/monitoring/monitors/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  static async runMonitorNow(id) {
    return this.makeRequest(`/monitoring/monitors/${encodeURIComponent(id)}/run`, { method: 'POST', body: '{}' });
  }

  static async pauseMonitor(id) {
    return this.makeRequest(`/monitoring/monitors/${encodeURIComponent(id)}/pause`, { method: 'POST', body: '{}' });
  }

  static async resumeMonitor(id) {
    return this.makeRequest(`/monitoring/monitors/${encodeURIComponent(id)}/resume`, { method: 'POST', body: '{}' });
  }

  static async getMonitorRuns(id, { limit = 30 } = {}) {
    return this.makeRequest(`/monitoring/monitors/${encodeURIComponent(id)}/runs?limit=${encodeURIComponent(limit)}`);
  }

  static async getMonitorTests(id, { days = 7 } = {}) {
    return this.makeRequest(`/monitoring/monitors/${encodeURIComponent(id)}/tests?days=${encodeURIComponent(days)}`, { suppressPlanUpgrade: true });
  }

  static async muteMonitorCase(id, { caseId, hours = 24, note = '' }) {
    return this.makeRequest(`/monitoring/monitors/${encodeURIComponent(id)}/mute`, { method: 'POST', body: JSON.stringify({ caseId, hours, note }) });
  }

  static async unmuteMonitorCase(id, caseId) {
    return this.makeRequest(`/monitoring/monitors/${encodeURIComponent(id)}/mute/${encodeURIComponent(caseId)}`, { method: 'DELETE' });
  }

  static async getMonitoringAnalytics({ days = 7, monitorId } = {}) {
    const qp = new URLSearchParams({ days: String(days) });
    if (monitorId) qp.set('monitorId', monitorId);
    return this.makeRequest(`/monitoring/analytics?${qp.toString()}`, { suppressPlanUpgrade: true });
  }

  static async applyMonitorLabel(caseIds, { remove = false } = {}) {
    return this.makeRequest('/monitoring/label/apply', { method: 'POST', body: JSON.stringify({ caseIds, remove }) });
  }

  static async getMonitoringSlackChannels() {
    return this.makeRequest('/monitoring/slack/channels', { suppressPlanUpgrade: true });
  }

  static async postMonitoringStatus({ channelId = '', days = 1 } = {}) {
    return this.makeRequest('/monitoring/slack/status', { method: 'POST', body: JSON.stringify({ channelId, days }) });
  }

  static async getMonitoringReport({ month = '' } = {}) {
    return this.makeRequest(`/monitoring/report${month ? `?month=${encodeURIComponent(month)}` : ''}`, { suppressPlanUpgrade: true });
  }

  static monitoringReportHtmlUrl({ month = '' } = {}) {
    const qp = new URLSearchParams({ format: 'html', download: '1', ...(month ? { month } : {}) });
    return `${this.getEffectiveBaseUrl()}/monitoring/report?${qp.toString()}`;
  }

  static async postMonitoringReport({ month = '', channelId = '' } = {}) {
    return this.makeRequest('/monitoring/report/post', { method: 'POST', body: JSON.stringify({ month, channelId }) });
  }

  static async compareMonitors(a, b) {
    return this.makeRequest(`/monitoring/compare?a=${encodeURIComponent(a)}&b=${encodeURIComponent(b)}`, { suppressPlanUpgrade: true });
  }

  static async setMonitoringStatusPage({ enabled = true, rotate = false, title } = {}) {
    return this.makeRequest('/monitoring/share', { method: 'PUT', body: JSON.stringify({ enabled, rotate, ...(title !== undefined ? { title } : {}) }) });
  }

  static async getPublicStatus(token) {
    return this.makeRequest(`/public/status/${encodeURIComponent(token)}`);
  }

  static async sendMonitoringEscalationTest(target) {
    return this.makeRequest('/monitoring/escalation/test', { method: 'POST', body: JSON.stringify({ target }) });
  }

  static async sendMonitoringSlackTest(channelId) {
    return this.makeRequest('/monitoring/slack/test', { method: 'POST', body: JSON.stringify({ channelId: channelId || '' }) });
  }

  /** Hide a failure signature from actionable lists (no heal / nothing to do). */
  static async dismissFailure(id, { dismissed = true } = {}) {
    return this.makeRequest(`/failures/${encodeURIComponent(id)}/dismiss`, {
      method: 'POST',
      body: JSON.stringify({ dismissed }),
    });
  }

  static async analyzeTestRun(id) {
    return this.makeRequest(`/testruns/${encodeURIComponent(id)}/analyze`, { method: 'POST', body: '{}' });
  }

  static async getJourneyMatrix(planId, { environmentId } = {}) {
    const qp = new URLSearchParams();
    if (environmentId) qp.set('environmentId', environmentId);
    const qs = qp.toString();
    return this.makeRequest(`/testplans/${encodeURIComponent(planId)}/journey-matrix${qs ? `?${qs}` : ''}`);
  }

  static async certifyTestPlan(planId, body = {}) {
    return this.makeRequest(`/testplans/${encodeURIComponent(planId)}/certify`, {
      method: 'POST',
      body: JSON.stringify(body || {}),
    });
  }

  static async getCertificationStatus(planId, { environmentId } = {}) {
    const qp = new URLSearchParams();
    if (environmentId) qp.set('environmentId', environmentId);
    const qs = qp.toString();
    return this.makeRequest(`/testplans/${encodeURIComponent(planId)}/certification${qs ? `?${qs}` : ''}`);
  }

  static async reopenTestPlan(planId) {
    return this.makeRequest(`/testplans/${encodeURIComponent(planId)}/reopen`, {
      method: 'POST',
      body: '{}',
    });
  }

  static async getCoverageGraph({ planId, projectId, refresh } = {}) {
    const qp = new URLSearchParams();
    if (planId) qp.set('planId', planId);
    if (projectId) qp.set('projectId', projectId);
    if (refresh) qp.set('refresh', 'true');
    const qs = qp.toString();
    return this.makeRequest(`/coverage/graph${qs ? `?${qs}` : ''}`);
  }

  static _coverageIntelCache = new Map();

  /**
   * A coverage intelligence payload is only usable when it carries the route
   * items and KPIs the UI renders. makeRequest resolves `{}` for an empty or
   * non-JSON 2xx body (proxy hiccup, restart mid-request), and caching that
   * used to crash the Coverage tab on `items.length` until the session
   * storage was cleared.
   */
  static isCoverageIntelligencePayload(data) {
    return !!data && typeof data === 'object' && Array.isArray(data.items) && !!data.kpis && typeof data.kpis === 'object';
  }

  static _forgetCoverageIntelligence(cacheKey) {
    this._coverageIntelCache.delete(cacheKey);
    try { sessionStorage.removeItem(cacheKey); } catch {}
  }

  static getLocalCachedCoverageIntelligence({ planId, projectId, environmentId, timeRange = '30d' } = {}) {
    const cacheKey = `cov_intel_${planId || 'all'}_${projectId || 'all'}_${environmentId || 'all'}_${timeRange}`;
    const mem = this._coverageIntelCache.get(cacheKey);
    if (mem?.data) {
      if (this.isCoverageIntelligencePayload(mem.data)) return mem.data;
      this._forgetCoverageIntelligence(cacheKey);
    }
    try {
      const raw = sessionStorage.getItem(cacheKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (this.isCoverageIntelligencePayload(parsed?.data)) {
          this._coverageIntelCache.set(cacheKey, { data: parsed.data, ts: parsed.ts || Date.now(), syncing: false });
          return parsed.data;
        }
        this._forgetCoverageIntelligence(cacheKey);
      }
    } catch {
      this._forgetCoverageIntelligence(cacheKey);
    }
    return null;
  }

  static async getCoverageIntelligence({ planId, projectId, environmentId, timeRange = '30d', refresh = false } = {}) {
    const qp = new URLSearchParams();
    if (planId) qp.set('planId', planId);
    if (projectId) qp.set('projectId', projectId);
    if (environmentId) qp.set('environmentId', environmentId);
    if (timeRange) qp.set('timeRange', timeRange);
    if (refresh) qp.set('refresh', 'true');
    const qs = qp.toString();
    const endpoint = `/coverage/intelligence${qs ? `?${qs}` : ''}`;
    const cacheKey = `cov_intel_${planId || 'all'}_${projectId || 'all'}_${environmentId || 'all'}_${timeRange}`;

    const cached = this._coverageIntelCache.get(cacheKey);
    if (!refresh && this.isCoverageIntelligencePayload(cached?.data) && (Date.now() - cached.ts < 10 * 60 * 1000)) {
      if (Date.now() - cached.ts > 15 * 1000 && !cached.syncing) {
        cached.syncing = true;
        this.makeRequest(endpoint)
          .then((fresh) => {
            if (!this.isCoverageIntelligencePayload(fresh)) {
              // Keep serving the last good payload rather than replacing it with junk.
              if (this._coverageIntelCache.has(cacheKey)) this._coverageIntelCache.get(cacheKey).syncing = false;
              return;
            }
            this._coverageIntelCache.set(cacheKey, { data: fresh, ts: Date.now(), syncing: false });
            try { sessionStorage.setItem(cacheKey, JSON.stringify({ data: fresh, ts: Date.now() })); } catch {}
          })
          .catch(() => {
            if (this._coverageIntelCache.has(cacheKey)) {
              this._coverageIntelCache.get(cacheKey).syncing = false;
            }
          });
      }
      return cached.data;
    }
    if (cached && !this.isCoverageIntelligencePayload(cached.data)) this._forgetCoverageIntelligence(cacheKey);

    const data = await this.makeRequest(endpoint);
    if (!this.isCoverageIntelligencePayload(data)) {
      this._forgetCoverageIntelligence(cacheKey);
      const err = new Error(data?.message || 'Coverage intelligence returned an empty response. Please retry.');
      err.status = 502;
      throw err;
    }
    this._coverageIntelCache.set(cacheKey, { data, ts: Date.now(), syncing: false });
    try { sessionStorage.setItem(cacheKey, JSON.stringify({ data, ts: Date.now() })); } catch {}
    return data;
  }

  static prefetchCoverageIntelligence(params = {}) {
    try {
      this.getCoverageIntelligence({ ...params, refresh: false }).catch(() => {});
    } catch {}
  }

  static async getGitHubConfig() {
    return this.makeRequest('/github/config');
  }

  static async generateCoverageTestPr(payload = {}) {
    return this.makeRequest('/coverage/generate-test-pr', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  }

  /** Attach an existing GitHub pull request to a coverage route so it acts as its regression guard. */
  static async linkCoverageGeneration(payload = {}) {
    return this.makeRequest('/coverage/generations/link', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  }

  static async getCoverageSettings({ projectId } = {}) {
    const qp = new URLSearchParams();
    if (projectId) qp.set('projectId', projectId);
    const qs = qp.toString();
    return this.makeRequest(`/coverage/settings${qs ? `?${qs}` : ''}`);
  }

  static async updateCoverageSettings({ projectId, coverageSettings = {} } = {}) {
    return this.makeRequest('/coverage/settings', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ projectId, coverageSettings }),
    });
  }

  static async getConfidenceReport({ planId, environmentId } = {}) {
    const qp = new URLSearchParams();
    if (planId) qp.set('planId', planId);
    if (environmentId) qp.set('environmentId', environmentId);
    const qs = qp.toString();
    return this.makeRequest(`/confidence/report${qs ? `?${qs}` : ''}`);
  }

  static async githubPrReview(body = {}) {
    return this.makeRequest('/integrations/github/pr-review', {
      method: 'POST',
      body: JSON.stringify(body || {}),
    });
  }

  /** Generate a Debug Pack–style HTML case report Artifact + public share link. */
  static async requestCaseReport(runId, caseId, body = {}) {
    return this.makeRequest(
      `/testruns/${encodeURIComponent(runId)}/cases/${encodeURIComponent(caseId)}/report`,
      {
        method: 'POST',
        body: JSON.stringify(body || {}),
      }
    );
  }

  static async patchTestRunResults(id, results, { finalize = false, endDate } = {}) {
    // Omit empty attachment arrays to keep payloads tiny on Pass/Fail
    const slim = (Array.isArray(results) ? results : []).map((r) => {
      if (!r || typeof r !== 'object') return r;
      const row = { ...r };
      if (Array.isArray(row.stepResults)) {
        row.stepResults = row.stepResults.map((sr) => {
          if (!sr || typeof sr !== 'object') return sr;
          if (!Array.isArray(sr.attachments) || sr.attachments.length === 0) {
            const { attachments: _a, ...rest } = sr;
            return rest;
          }
          return sr;
        });
      }
      return row;
    });
    const body = { results: slim };
    if (finalize) body.finalize = true;
    if (endDate) body.endDate = endDate;

    const maxAttempts = 3;
    let lastErr;
    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
      try {
        return await this.makeRequest(`/testruns/${id}/results`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        });
      } catch (e) {
        lastErr = e;
        const retryable = e instanceof TypeError
          || e?.status === 0
          || e?.status === 502
          || e?.status === 503
          || e?.status === 504
          || /Network error|server unreachable|Gateway timeout|timed out/i.test(e?.message || '');
        if (!retryable || attempt === maxAttempts) throw e;
        await new Promise((r) => setTimeout(r, 300 * attempt));
      }
    }
    throw lastErr;
  }

  static async deleteTestRun(id) {
    return this.makeRequest(`/testruns/${id}`, {
      method: 'DELETE',
    });
  }

  // Archive helpers with offline fallback
  static async archiveTestRun(id) {
    devLog.log('[Archive] Request', id);
    try {
      return await this.makeRequest(`/testruns/${id}/archive`, { method: 'PATCH' });
    } catch (e) {
      if (e instanceof TypeError) {
        // Network unreachable -> queue operation
        const queue = loadOfflineQueue();
        if (!queue.find(q => q.id === id && q.action === 'archive')) {
          queue.push({ id, action: 'archive', ts: Date.now() });
          saveOfflineQueue(queue);
          devLog.warn('[Archive] Queued offline archive op for', id);
        }
        // Return optimistic object signature so UI can proceed
        return {
          _id: id,
          archived: true,
          archivedDate: new Date().toISOString().split('T')[0],
          offlinePending: true
        };
      }
      throw e;
    }
  }

  static async unarchiveTestRun(id) {
    devLog.log('[Unarchive] Request', id);
    try {
      return await this.makeRequest(`/testruns/${id}/unarchive`, { method: 'PATCH' });
    } catch (e) {
      if (e instanceof TypeError) {
        const queue = loadOfflineQueue();
        if (!queue.find(q => q.id === id && q.action === 'unarchive')) {
          queue.push({ id, action: 'unarchive', ts: Date.now() });
          saveOfflineQueue(queue);
          devLog.warn('[Unarchive] Queued offline unarchive op for', id);
        }
        return {
            _id: id,
            archived: false,
            archivedDate: undefined,
            offlinePending: true
        };
      }
      throw e;
    }
  }

  // Users
  static async getUsers() {
    return this.makeRequest('/users');
  }

  static async createUser(userData) { // NEW: admin creates user in same tenant
    return this.makeRequest('/users', {
      method: 'POST',
      body: JSON.stringify(userData)
    });
  }

  static async updateUser(id, userData) {
    return this.makeRequest(`/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(userData),
    });
  }

  static async deleteUser(id) {
    return this.makeRequest(`/users/${id}`, {
      method: 'DELETE',
    });
  }

  static async login(email, password) {
    // On a branded subdomain (<slug>.testera.io), scope the login to that tenant.
    const tenantSlug = getTenantSlug();
    const data = await this.makeRequest('/users/login', {
      method: 'POST',
      body: JSON.stringify(tenantSlug ? { email, password, tenantSlug } : { email, password }),
    });
    if (data.token) {
      this.setToken(data.token);
      this.confirmPendingCheckoutSession().catch(() => {});
    }
    return data;
  }

  /** Best-effort server audit for logout; JWT remains client-held until cleared. */
  static async logout() {
    try {
      await this.makeRequest('/users/logout', { method: 'POST', body: '{}' });
    } catch {
      /* ignore — still clear local session */
    }
  }

  /** Presence heartbeat + notable UI surface (automation, settings, …). */
  static async reportActivity({ surface, path } = {}) {
    try {
      return await this.makeRequest('/users/me/activity', {
        method: 'POST',
        body: JSON.stringify({
          surface: surface || undefined,
          path: path || (typeof window !== 'undefined' ? window.location?.pathname : undefined),
        }),
      });
    } catch {
      return null;
    }
  }

  static async listSessions(params = {}) {
    const qp = new URLSearchParams();
    Object.entries(params || {}).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qp.set(k, v);
    });
    const qs = qp.toString();
    return this.makeRequest(`/sessions${qs ? `?${qs}` : ''}`);
  }

  static async revokeSession(sessionId) {
    return this.makeRequest(`/sessions/${encodeURIComponent(sessionId)}/revoke`, {
      method: 'POST',
      body: '{}',
    });
  }

  /** Platform admin: sessions across all tenants. */
  static async listAdminSessions(params = {}) {
    const qp = new URLSearchParams();
    Object.entries(params || {}).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qp.set(k, v);
    });
    const qs = qp.toString();
    return this.makeRequest(`/admin/sessions${qs ? `?${qs}` : ''}`);
  }

  static async revokeAdminSession(sessionId) {
    return this.makeRequest(`/admin/sessions/${encodeURIComponent(sessionId)}/revoke`, {
      method: 'POST',
      body: '{}',
    });
  }

  /** Public tenant branding for co-branded subdomain login pages. */
  static getPublicBranding(slug) {
    return this.makeRequest(`/public/branding?slug=${encodeURIComponent(slug)}`);
  }

  // 2FA: pass mfaToken from a login challenge, OR omit it when already authenticated
  // (Security settings enrollment uses the session Bearer token).
  static async setup2FA(mfaToken) {
    const body = mfaToken ? { mfaToken } : {};
    return this.makeRequest('/users/2fa/setup', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  static async verify2FA({ mfaToken, code } = {}) {
    const body = { code };
    if (mfaToken) body.mfaToken = mfaToken;
    const data = await this.makeRequest('/users/2fa/verify', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    if (data.token) {
      this.setToken(data.token);
      this.confirmPendingCheckoutSession().catch(() => {});
    }
    return data;
  }

  static getGoogleAuthConfig() {
    // Config is static per deployment — cache to avoid refetching on remounts
    return this.memoGet('/auth/google/config', 5 * 60 * 1000);
  }

  static getGoogleAuthStartUrl(mode = 'login') {
    const base = this.getEffectiveBaseUrl();
    const returnTo = encodeURIComponent(
      typeof window !== 'undefined' ? `${window.location.origin}${window.location.pathname}` : ''
    );
    return `${base}/auth/google?mode=${encodeURIComponent(mode)}&returnTo=${returnTo}`;
  }

  static getMicrosoftAuthConfig() {
    return this.memoGet('/auth/microsoft/config', 5 * 60 * 1000);
  }

  static getMicrosoftAuthStartUrl(mode = 'login') {
    const base = this.getEffectiveBaseUrl();
    const returnTo = encodeURIComponent(
      typeof window !== 'undefined' ? `${window.location.origin}${window.location.pathname}` : ''
    );
    return `${base}/auth/microsoft?mode=${encodeURIComponent(mode)}&returnTo=${returnTo}`;
  }

  static async signup(userData) {
    const data = await this.makeRequest('/users/signup', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
    // When email verification is off (campaign), server returns a session token.
    if (data?.token) {
      this.setToken(data.token);
      this.confirmPendingCheckoutSession().catch(() => {});
    }
    return data;
  }

  // Public: resend verification email (no auth required — called before login)
  static async resendVerificationEmail(email) {
    return this.makeRequest('/users/resend-verification', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  static async forgotPassword(email) {
    return this.makeRequest('/users/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email })
    });
  }
  static async validateResetToken({ email, token }) { // NEW
    return this.makeRequest('/users/validate-reset-token', {
      method: 'POST',
      body: JSON.stringify({ email, token })
    });
  }
  static async resetPassword({ email, token, password }) {
    return this.makeRequest('/users/reset-password', {
      method: 'POST',
      body: JSON.stringify({ email, token, password })
    });
  }

  static async changePassword({ currentPassword, newPassword }) {
    return this.makeRequest('/users/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword })
    });
  }

  // Email verification
  static async sendVerificationEmail() {
    return this.makeRequest('/users/send-verification', { method: 'POST' });
  }

  static async verifyEmail({ token, email }) {
    const qp = new URLSearchParams();
    qp.set('token', token);
    qp.set('email', email);
    return this.makeRequest(`/users/verify-email?${qp.toString()}`);
  }

  static async getMe() {
    return this.memoGet('/users/me');
  }

  // NEW: Update current user's profile (name/email)
  static async updateMe(data) {
    return this.makeRequest('/users/me', {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  // API Keys
  static async getApiKeys() { return this.makeRequest('/users/api-keys'); }
  static async createApiKey(label) { return this.makeRequest('/users/api-keys', { method: 'POST', body: JSON.stringify({ label }) }); }
  static async updateApiKeyLabel(keyId, label) { return this.makeRequest(`/users/api-keys/${keyId}`, { method: 'PATCH', body: JSON.stringify({ label }) }); }
  static async revokeApiKey(keyId) { return this.makeRequest(`/users/api-keys/${keyId}`, { method: 'DELETE' }); }

  // Invitations (seat-based)
  static async getInvitations() { return this.makeRequest('/users/invitations'); }
  static async createInvitation({ email, role }) { return this.makeRequest('/users/invitations', { method: 'POST', body: JSON.stringify({ email, role }) }); }
  static async revokeInvitation(id) { return this.makeRequest(`/users/invitations/${id}/revoke`, { method: 'POST' }); }
  static async resendInvitation(id) { return this.makeRequest(`/users/invitations/${id}/resend`, { method: 'POST' }); }
  static async bulkRevokeExpiredInvitations() { return this.makeRequest('/users/invitations/bulk-revoke-expired', { method: 'POST' }); }
  static async lookupInvitation({ token, email }) {
    const qp = new URLSearchParams();
    qp.set('token', token);
    if (email) qp.set('email', email);
    return this.makeRequest(`/invitations/lookup?${qp.toString()}`);
  }

  // Test Labels
  static async getTestLabels() { return this.makeRequest('/testlabels'); }
  static async createTestLabel(data) { return this.makeRequest('/testlabels', { method: 'POST', body: JSON.stringify(data) }); }
  static async updateTestLabel(id, data) { return this.makeRequest(`/testlabels/${id}`, { method: 'PUT', body: JSON.stringify(data) }); }
  static async deleteTestLabel(id) { return this.makeRequest(`/testlabels/${id}`, { method: 'DELETE' }); }

  // Test Environments
  static async getEnvironments(opts = {}) {
    const qs = opts.revealSecrets ? '?revealSecrets=true' : '';
    return this.makeRequest(`/environments${qs}`);
  }
  static async getEnvironment(id) { return this.makeRequest(`/environments/${id}`); }
  static async createEnvironment(data) { return this.makeRequest('/environments', { method: 'POST', body: JSON.stringify(data) }); }
  static async updateEnvironment(id, data) { return this.makeRequest(`/environments/${id}`, { method: 'PUT', body: JSON.stringify(data) }); }
  static async deleteEnvironment(id) { return this.makeRequest(`/environments/${id}`, { method: 'DELETE' }); }

  static async getTestRun(id) {
    return this.makeRequest(`/testruns/${encodeURIComponent(id)}`);
  }

  static async compareTestRuns(runA, runB) {
    const qp = new URLSearchParams({ runA, runB });
    return this.makeRequest(`/testruns/compare?${qp.toString()}`);
  }

  static async compareRunsByEnvironment({ testPlanId, environmentIdA, environmentIdB, runA, runB }) {
    const qp = new URLSearchParams({ testPlanId });
    if (runA && runB) {
      qp.set('runA', runA);
      qp.set('runB', runB);
    } else {
      qp.set('environmentIdA', environmentIdA);
      qp.set('environmentIdB', environmentIdB);
    }
    return this.makeRequest(`/testruns/compare-environments?${qp.toString()}`);
  }

  // TASKS (tenant-wide)
  static async getTasks(params = {}) {
    const qp = new URLSearchParams();
    Object.entries(params || {}).forEach(([k, v]) => {
      if (v === undefined || v === null || v === '') return;
      qp.set(k, v);
    });
    const qs = qp.toString();
    return this.memoGet(`/tasks${qs ? ('?' + qs) : ''}`);
  }

  static async createTask(taskData) {
    this.invalidateMemo('/tasks');
    return this.makeRequest('/tasks', {
      method: 'POST',
      body: JSON.stringify(taskData || {})
    });
  }

  static async updateTask(id, taskData) {
    this.invalidateMemo('/tasks');
    return this.makeRequest(`/tasks/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(taskData || {})
    });
  }

  /** Bulk archive: { mode: 'completed'|'stale', olderThanDays?, dryRun? }. Dry run returns { count, sample }. */
  static async archiveTasks(body = {}) {
    if (!body.dryRun) this.invalidateMemo('/tasks');
    return this.makeRequest('/tasks/archive', { method: 'POST', body: JSON.stringify(body || {}) });
  }

  static async deleteTask(id) {
    this.invalidateMemo('/tasks');
    return this.makeRequest(`/tasks/${encodeURIComponent(id)}`, {
      method: 'DELETE'
    });
  }

  // FREE DOCUMENTS (tenant-wide)
  static async getDocuments(params = {}) {
    const qp = new URLSearchParams();
    Object.entries(params || {}).forEach(([k, v]) => {
      if (v === undefined || v === null || v === '') return;
      qp.set(k, v);
    });
    const qs = qp.toString();
    return this.makeRequest(`/documents${qs ? ('?' + qs) : ''}`);
  }

  static async getDocument(id) {
    return this.makeRequest(`/documents/${encodeURIComponent(id)}`);
  }

  static async createDocument(docData) {
    return this.makeRequest('/documents', {
      method: 'POST',
      body: JSON.stringify(docData || {})
    });
  }

  static async updateDocument(id, docData) {
    return this.makeRequest(`/documents/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(docData || {})
    });
  }

  static async reorderDocuments(orderedIds = []) {
    return this.makeRequest('/documents/reorder', {
      method: 'PUT',
      body: JSON.stringify({ orderedIds }),
    });
  }

  static async deleteDocument(id) {
    return this.makeRequest(`/documents/${encodeURIComponent(id)}`, {
      method: 'DELETE'
    });
  }

  static async refreshDocument(id) {
    return this.makeRequest(`/documents/${encodeURIComponent(id)}/refresh`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  static async getDoraBoard(params = {}) {
    const qp = new URLSearchParams();
    if (params.refresh) qp.set('refresh', '1');
    const qs = qp.toString();
    return this.makeRequest(`/dora/board${qs ? `?${qs}` : ''}`);
  }

  static async refreshDoraBoard() {
    return this.makeRequest('/dora/refresh', {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  static async updateDoraSettings(body = {}) {
    return this.makeRequest('/dora/settings', {
      method: 'PATCH',
      body: JSON.stringify(body || {}),
    });
  }

  // WEBHOOK STUDIO
  static async getWebhookInboxes() {
    return this.makeRequest('/webhook-studio/inboxes');
  }

  static async createWebhookInbox(data) {
    return this.makeRequest('/webhook-studio/inboxes', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  static async getWebhookInbox(id) {
    return this.makeRequest(`/webhook-studio/inboxes/${encodeURIComponent(id)}`);
  }

  static async updateWebhookInbox(id, data) {
    return this.makeRequest(`/webhook-studio/inboxes/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  static async deleteWebhookInbox(id) {
    return this.makeRequest(`/webhook-studio/inboxes/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }

  static async getWebhookRequests(inboxId, params = {}) {
    const qp = new URLSearchParams();
    if (params.page) qp.set('page', String(params.page));
    if (params.limit) qp.set('limit', String(params.limit));
    if (params.method) qp.set('method', params.method);
    if (params.q) qp.set('q', params.q);
    const qs = qp.toString();
    return this.makeRequest(`/webhook-studio/inboxes/${encodeURIComponent(inboxId)}/requests${qs ? `?${qs}` : ''}`);
  }

  static async getRecentWebhookRequests(params = {}) {
    const qp = new URLSearchParams();
    if (params.limit) qp.set('limit', String(params.limit));
    if (params.since) qp.set('since', String(params.since));
    const qs = qp.toString();
    return this.makeRequest(`/webhook-studio/recent-requests${qs ? `?${qs}` : ''}`);
  }

  static async clearWebhookRequests(inboxId) {
    return this.makeRequest(`/webhook-studio/inboxes/${encodeURIComponent(inboxId)}/requests`, {
      method: 'DELETE',
    });
  }

  static async getWebhookRequest(requestId) {
    return this.makeRequest(`/webhook-studio/requests/${encodeURIComponent(requestId)}`);
  }

  static async deleteWebhookRequest(requestId) {
    return this.makeRequest(`/webhook-studio/requests/${encodeURIComponent(requestId)}`, {
      method: 'DELETE',
    });
  }

  static async replayWebhookRequest(requestId, data) {
    return this.makeRequest(`/webhook-studio/requests/${encodeURIComponent(requestId)}/replay`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  static async verifyWebhookSignature(requestId, data) {
    return this.makeRequest(`/webhook-studio/requests/${encodeURIComponent(requestId)}/verify-signature`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  static async runWebhookTraceDiagnostics(data) {
    return this.makeRequest('/webhook-studio/diagnostics/trace', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // MOCK STUDIO
  static async getMockServices() {
    return this.makeRequest('/mock-studio/services');
  }

  static async createMockService(data) {
    return this.makeRequest('/mock-studio/services', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  static async getMockService(id) {
    return this.makeRequest(`/mock-studio/services/${encodeURIComponent(id)}`);
  }

  static async updateMockService(id, data) {
    return this.makeRequest(`/mock-studio/services/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  static async deleteMockService(id) {
    return this.makeRequest(`/mock-studio/services/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }

  static async resetMockScenarios(id) {
    return this.makeRequest(`/mock-studio/services/${encodeURIComponent(id)}/reset-scenarios`, {
      method: 'POST',
    });
  }

  static async getMockRules(serviceId) {
    return this.makeRequest(`/mock-studio/services/${encodeURIComponent(serviceId)}/rules`);
  }

  static async createMockRule(serviceId, data) {
    return this.makeRequest(`/mock-studio/services/${encodeURIComponent(serviceId)}/rules`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  static async updateMockRule(serviceId, ruleId, data) {
    return this.makeRequest(`/mock-studio/services/${encodeURIComponent(serviceId)}/rules/${encodeURIComponent(ruleId)}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  static async deleteMockRule(serviceId, ruleId) {
    return this.makeRequest(`/mock-studio/services/${encodeURIComponent(serviceId)}/rules/${encodeURIComponent(ruleId)}`, {
      method: 'DELETE',
    });
  }

  static async reorderMockRules(serviceId, ruleIds) {
    return this.makeRequest(`/mock-studio/services/${encodeURIComponent(serviceId)}/rules/reorder`, {
      method: 'POST',
      body: JSON.stringify({ ruleIds }),
    });
  }

  static async getMockLogs(serviceId, params = {}) {
    const qp = new URLSearchParams();
    if (params.limit) qp.set('limit', String(params.limit));
    if (params.method) qp.set('method', params.method);
    if (params.status) qp.set('status', String(params.status));
    if (params.ruleId) qp.set('ruleId', params.ruleId);
    const qs = qp.toString();
    return this.makeRequest(`/mock-studio/services/${encodeURIComponent(serviceId)}/logs${qs ? `?${qs}` : ''}`);
  }

  static async clearMockLogs(serviceId) {
    return this.makeRequest(`/mock-studio/services/${encodeURIComponent(serviceId)}/logs`, {
      method: 'DELETE',
    });
  }

  static async testMockMatch(serviceId, data) {
    return this.makeRequest(`/mock-studio/services/${encodeURIComponent(serviceId)}/test-match`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  static async getMockPresets() {
    return this.makeRequest('/mock-studio/presets');
  }

  // PLUGINS
  static async getPlugins() {
    return this.makeRequest('/plugins');
  }

  static async updatePlugin(id, body) {
    return this.makeRequest(`/plugins/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(body || {}),
    });
  }

  static async updateCustomPluginSharing(id, body) {
    return this.makeRequest(`/plugins/${encodeURIComponent(id)}/sharing`, {
      method: 'PATCH',
      body: JSON.stringify(body || {}),
    });
  }

  // ADMIN PLUGINS (Multi-tenant availability & custom plugins)
  static async getAdminPlugins() {
    return this.makeRequest('/admin/plugins', { method: 'GET' });
  }

  static async updateAdminPlugin(id, body) {
    return this.makeRequest(`/admin/plugins/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(body || {}),
    });
  }

  static async createAdminPlugin(body) {
    return this.makeRequest('/admin/plugins', {
      method: 'POST',
      body: JSON.stringify(body || {}),
    });
  }

  static async deleteAdminPlugin(id) {
    return this.makeRequest(`/admin/plugins/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }

  // SELF-HEAL JOBS
  static async getHealJobs(params = {}) {
    const qp = new URLSearchParams();
    Object.entries(params || {}).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qp.set(k, v);
    });
    const qs = qp.toString();
    return this.makeRequest(`/heal-jobs${qs ? `?${qs}` : ''}`, { suppressPlanUpgrade: true });
  }

  static async getHealSetup() {
    return this.makeRequest('/heal-jobs/setup', { suppressPlanUpgrade: true });
  }

  /** Open a PR with the portable self-heal.yml + script in the linked GitHub repo (any tenant). */
  static async deployHealSetup(body = {}) {
    return this.makeRequest('/heal-jobs/setup', {
      method: 'POST',
      body: JSON.stringify(body || {}),
    });
  }

  static async createHealJob(body = {}) {
    return this.makeRequest('/heal-jobs', {
      method: 'POST',
      body: JSON.stringify(body || {}),
    });
  }

  static async patchHealJob(id, body = {}) {
    return this.makeRequest(`/heal-jobs/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(body || {}),
    });
  }

  // AUTOMATION TEMPLATES (workspace custom; built-ins are client-side)
  static async getAutomationTemplates() {
    return this.makeRequest('/automations/templates', { suppressPlanUpgrade: true });
  }

  static async createAutomationTemplate(data) {
    return this.makeRequest('/automations/templates', {
      method: 'POST',
      body: JSON.stringify(data || {}),
    });
  }

  static async updateAutomationTemplate(id, data) {
    return this.makeRequest(`/automations/templates/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(data || {}),
    });
  }

  static async deleteAutomationTemplate(id) {
    return this.makeRequest(`/automations/templates/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }

  // AUTOMATION RECIPES
  static async getAutomationRecipes() {
    return this.makeRequest('/automations/recipes', { suppressPlanUpgrade: true });
  }

  static async getAutomationRecipe(id) {
    return this.makeRequest(`/automations/recipes/${encodeURIComponent(id)}`);
  }

  static async createAutomationRecipe(data) {
    return this.makeRequest('/automations/recipes', {
      method: 'POST',
      body: JSON.stringify(data || {}),
    });
  }

  static async updateAutomationRecipe(id, data) {
    return this.makeRequest(`/automations/recipes/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(data || {}),
    });
  }

  static async deleteAutomationRecipe(id) {
    return this.makeRequest(`/automations/recipes/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }

  static async pauseAutomationRecipe(id) {
    return this.makeRequest(`/automations/recipes/${encodeURIComponent(id)}/pause`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  static async resumeAutomationRecipe(id) {
    return this.makeRequest(`/automations/recipes/${encodeURIComponent(id)}/resume`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  static async getAutomationRuns(id, params = {}) {
    const qp = new URLSearchParams();
    Object.entries(params || {}).forEach(([k, v]) => {
      if (v === undefined || v === null || v === '') return;
      qp.set(k, v);
    });
    const qs = qp.toString();
    return this.makeRequest(`/automations/recipes/${encodeURIComponent(id)}/runs${qs ? (`?${qs}`) : ''}`, { suppressPlanUpgrade: true });
  }

  static async runAutomationRecipe(id) {
    return this.makeRequest(`/automations/recipes/${encodeURIComponent(id)}/run`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  static async getDocumentShare(id) {
    return this.makeRequest(`/documents/${encodeURIComponent(id)}/share`);
  }

  static async updateDocumentShare(id, body = {}) {
    return this.makeRequest(`/documents/${encodeURIComponent(id)}/share`, {
      method: 'PUT',
      body: JSON.stringify(body || {}),
    });
  }

  /** Public (no auth) shared document by opaque token. */
  // TREND ANALYTICS
  static async getAnalyticsTrends({ weeks = 26, refresh = false } = {}) {
    const qp = new URLSearchParams({ weeks: String(weeks) });
    if (refresh) qp.set('refresh', 'true');
    return this.makeRequest(`/analytics/trends?${qp.toString()}`);
  }

  static async getAnalyticsSettings() {
    return this.makeRequest('/analytics/settings');
  }

  static async updateAnalyticsSettings(body = {}) {
    return this.makeRequest('/analytics/settings', { method: 'PUT', body: JSON.stringify(body || {}) });
  }

  static async testAnalyticsJql(jql) {
    return this.makeRequest('/analytics/settings/test', { method: 'POST', body: JSON.stringify({ jql }) });
  }

  static async setAnalyticsShare(body = {}) {
    return this.makeRequest('/analytics/share', { method: 'PUT', body: JSON.stringify(body || {}) });
  }

  static async postAnalyticsToSlack(body = {}) {
    return this.makeRequest('/analytics/slack', { method: 'POST', body: JSON.stringify(body || {}) });
  }

  static async getPublicAnalytics(token) {
    return this.makeRequest(`/public/analytics/${encodeURIComponent(token)}`);
  }

  /** "Try live demo": short-lived session in the shared read-only demo workspace. */
  static async startDemoSession() {
    const data = await this.makeRequest('/demo/session', { method: 'POST' });
    if (data?.token) this.setToken(data.token);
    return data;
  }

  static async getPublicSharedDocument(token) {
    return this.makeRequest(`/public/documents/${encodeURIComponent(token)}`);
  }

  static async ingestPerfResults(payload) {
    return this.makeRequest('/perf/results', {
      method: 'POST',
      body: JSON.stringify(payload || {}),
    });
  }

  /** @deprecated Prefer ingestPerfResults({ framework: 'k6', ... }) */
  static async ingestK6Perf(payload) {
    return this.makeRequest('/perf/k6', {
      method: 'POST',
      body: JSON.stringify(payload || {}),
    });
  }

  static async listPerfSnapshots(params = {}) {
    const qs = new URLSearchParams();
    if (params.testRunId) qs.set('testRunId', params.testRunId);
    if (params.testPlanId) qs.set('testPlanId', params.testPlanId);
    if (params.scenarioTag) qs.set('scenarioTag', params.scenarioTag);
    if (params.canvasId) qs.set('canvasId', params.canvasId);
    if (params.framework) qs.set('framework', params.framework);
    if (params.limit) qs.set('limit', String(params.limit));
    const q = qs.toString();
    return this.makeRequest(`/perf/snapshots${q ? `?${q}` : ''}`);
  }

  static async ingestMetricEvents(payload) {
    return this.makeRequest('/metrics/events', {
      method: 'POST',
      body: JSON.stringify(payload || {}),
    });
  }

  // Sample data generation
  static async generateSampleData() {
    return this.makeRequest('/sample-data/generate', {
      method: 'POST',
    });
  }

  static async generateRichSampleData() {
    return this.makeRequest('/sample-data/generate-rich', {
      method: 'POST',
    });
  }

  static async importLocalData(payload) {
    return this.makeRequest('/sync/import', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  // Billing
  static async createCheckoutSession({ plan, quantity = 1, email, interval = 'month' } = {}) {
    return this.makeRequest('/billing/create-checkout-session', {
      method: 'POST',
      body: JSON.stringify({ plan, quantity, email, interval })
    });
  }
  static async confirmCheckoutSession(sessionId) {
    return this.makeRequest('/billing/confirm-session', {
      method: 'POST',
      body: JSON.stringify({ sessionId })
    });
  }
  // Confirm-session requires auth: if checkout finished before the user was
  // signed in (post-signup flow), remember the session and confirm after login.
  static setPendingCheckoutSession(sessionId) {
    try { sessionId ? localStorage.setItem('testara_pending_checkout_session', sessionId) : localStorage.removeItem('testara_pending_checkout_session'); } catch {}
  }
  static async confirmPendingCheckoutSession() {
    let sessionId = '';
    try { sessionId = localStorage.getItem('testara_pending_checkout_session') || ''; } catch {}
    if (!sessionId) return null;
    try {
      const res = await this.confirmCheckoutSession(sessionId);
      this.setPendingCheckoutSession('');
      return res;
    } catch (e) {
      // Keep the pending session only if it might succeed later (e.g. transient error)
      if (e?.status === 403 || e?.status === 404) this.setPendingCheckoutSession('');
      return null;
    }
  }
  static async getCheckoutSession(sessionId) {
    return this.makeRequest(`/billing/session/${encodeURIComponent(sessionId)}`);
  }

  // AI credits
  static async getAiCredits() {
    return this.makeRequest('/ai/credits');
  }
  static async createAiCreditsCheckout(credits) {
    return this.makeRequest('/billing/create-credits-checkout', {
      method: 'POST',
      body: JSON.stringify({ credits })
    });
  }
  static async confirmAiCreditsSession(sessionId) {
    return this.makeRequest('/billing/confirm-credits-session', {
      method: 'POST',
      body: JSON.stringify({ sessionId })
    });
  }
  // Superadmin
  static async getTenantAiUsage(tenantId) {
    return this.makeRequest(`/admin/tenants/${encodeURIComponent(tenantId)}/ai-usage`);
  }
  static async grantTenantAiCredits(tenantId, grant) {
    return this.makeRequest(`/admin/tenants/${encodeURIComponent(tenantId)}/ai-credits`, {
      method: 'POST',
      body: JSON.stringify({ grant })
    });
  }

  static async initOfflineSync() {
    // Flush immediately and on connectivity regain
    await flushOfflineQueue();
    window.addEventListener('online', () => {
      flushOfflineQueue();
    });
    // Periodic retry every 30s
    setInterval(() => {
      flushOfflineQueue();
    }, 30000);
  }

  static async getHealth() {
    return this.makeRequest('/health');
  }

  static async getAuditLogs(params = {}) {
    const { limit = 100, skip = 0, ...filters } = params;
    const qp = new URLSearchParams();
    qp.set('limit', limit);
    qp.set('skip', skip);
    Object.entries(filters).forEach(([k,v]) => {
      if (v === undefined || v === null || v === '') return;
      if (Array.isArray(v)) {
        if (v.length) qp.set(k, v.join(','));
      } else {
        qp.set(k, v);
      }
    });
    const key = qp.toString();
    const now = Date.now();
    // 10 second cache to prevent excessive calls with identical params
    if (this._auditLogsCache.data && this._auditLogsCache.paramsKey === key && (now - this._auditLogsCache.ts) < 10000) {
      return this._auditLogsCache.data;
    }
    const data = await this.makeRequest(`/audit-logs?${key}`);
    this._auditLogsCache = { ts: now, paramsKey: key, data };
    return data;
  }

  static openAuditLogStream(onMessage) {
    const base = `${this.getEffectiveBaseUrl()}`;
    let token = '';
    try {
      if (this.getActiveAuthMode() === 'apikey' && this.getActiveApiKey()) {
        token = this.getActiveApiKey();
      } else if (this.getToken()) {
        token = this.getToken();
      }
    } catch {}
    const url = token ? `${base}/audit-logs/stream?token=${encodeURIComponent(token)}` : `${base}/audit-logs/stream`;
    const es = new EventSource(url);
    es.onmessage = (evt) => {
      if (!evt.data) return;
      try { onMessage(JSON.parse(evt.data)); } catch { /* ignore */ }
    };
    es.onerror = () => {
      // Auto-reconnect logic: close and create new after delay
      try { es.close(); } catch {}
      setTimeout(() => ApiService.openAuditLogStream(onMessage), 2000);
    };
    return es;
  }

  static auditLogsExportUrl(format = 'json', limit = 5000, filters = {}) {
    const qp = new URLSearchParams();
    qp.set('format', format);
    qp.set('limit', limit);
    Object.entries(filters).forEach(([k,v]) => {
      if (v === undefined || v === null || v === '') return;
      if (Array.isArray(v)) {
        if (v.length) qp.set(k, v.join(','));
      } else {
        qp.set(k, v);
      }
    });
    return `${this.getEffectiveBaseUrl()}/audit-logs/export?${qp.toString()}`;
  }

  static async uploadFile(file) {
    const form = new FormData();
    form.append('file', file);
    // Build auth header without forcing Content-Type
    let auth = '';
    try {
      if (this.getActiveAuthMode() === 'apikey') {
        const k = this.getActiveApiKey();
        auth = k ? `Bearer ${k}` : (this.getToken() ? `Bearer ${this.getToken()}` : '');
      } else {
        auth = this.getToken() ? `Bearer ${this.getToken()}` : '';
      }
    } catch {}
    const headers = auth ? { Authorization: auth } : {};
    const res = await fetch(`${this.getEffectiveBaseUrl()}/uploads`, {
      method: 'POST',
      headers,
      body: form
    });
    if (!res.ok) {
      const errMsg = await res.text().catch(()=>'');
      throw new Error(errMsg || `Upload failed (${res.status})`);
    }
    return res.json();
  }

  // ALLURE REPORTS
  static buildBearerAuthHeader() {
    let auth = '';
    try {
      if (this.getActiveAuthMode() === 'apikey') {
        const k = this.getActiveApiKey();
        auth = k ? `Bearer ${k}` : (this.getToken() ? `Bearer ${this.getToken()}` : '');
      } else {
        auth = this.getToken() ? `Bearer ${this.getToken()}` : '';
      }
    } catch {}
    return auth;
  }

  static async uploadAllureReport(runId, file) {
    const form = new FormData();
    form.append('file', file);
    const auth = this.buildBearerAuthHeader();
    const headers = auth ? { Authorization: auth } : {};
    const res = await fetch(`${this.getEffectiveBaseUrl()}/testruns/${encodeURIComponent(runId)}/allure`, {
      method: 'POST',
      headers,
      body: form
    });
    if (!res.ok) {
      let msg = '';
      try { msg = (await res.json())?.message || ''; } catch {}
      throw new Error(msg || `Allure upload failed (${res.status})`);
    }
    return res.json();
  }

  static async getAllureReportHtml(runId) {
    const auth = this.buildBearerAuthHeader();
    const headers = auth ? { Authorization: auth } : {};
    const res = await fetch(`${this.getEffectiveBaseUrl()}/testruns/${encodeURIComponent(runId)}/allure/report`, { headers });
    if (!res.ok) {
      let msg = '';
      try { msg = (await res.json())?.message || ''; } catch {}
      throw new Error(msg || `Failed to load Allure report (${res.status})`);
    }
    return res.text();
  }

  static async deleteAllureReport(runId) {
    return this.makeRequest(`/testruns/${encodeURIComponent(runId)}/allure`, { method: 'DELETE' });
  }

  static async getAllureHistory(params = {}) {
    const qp = new URLSearchParams();
    Object.entries(params || {}).forEach(([k, v]) => {
      if (v === undefined || v === null || v === '') return;
      qp.set(k, v);
    });
    const qs = qp.toString();
    return this.makeRequest(`/allure/history${qs ? `?${qs}` : ''}`);
  }

  // UNIVERSAL REPORT IMPORTS
  static async uploadReportImport({ testRunId, files = [], idempotencyKey, metadata = {}, sourceTool, parserId } = {}) {
    const form = new FormData();
    for (const f of files) form.append('files[]', f);
    if (testRunId) form.append('testRunId', testRunId);
    if (sourceTool) form.append('sourceTool', sourceTool);
    if (parserId) form.append('parserId', parserId);
    if (metadata && Object.keys(metadata).length) form.append('metadata', JSON.stringify(metadata));
    const auth = this.buildBearerAuthHeader();
    const headers = auth ? { Authorization: auth } : {};
    if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey;
    const res = await fetch(`${this.getEffectiveBaseUrl()}/v1/report-imports`, {
      method: 'POST',
      headers,
      body: form,
    });
    if (!res.ok) {
      let msg = '';
      try { msg = (await res.json())?.message || ''; } catch {}
      throw new Error(msg || `Report import failed (${res.status})`);
    }
    return res.json();
  }

  static async getReportImports(params = {}) {
    const qp = new URLSearchParams();
    Object.entries(params || {}).forEach(([k, v]) => {
      if (v === undefined || v === null || v === '') return;
      qp.set(k, v);
    });
    const qs = qp.toString();
    return this.makeRequest(`/v1/report-imports${qs ? `?${qs}` : ''}`);
  }

  static async getReportImport(importId) {
    return this.makeRequest(`/v1/report-imports/${encodeURIComponent(importId)}`);
  }

  static async getImportedResults(runId) {
    return this.makeRequest(`/testruns/${encodeURIComponent(runId)}/imported-results`);
  }

  static async getReportImportParsers() {
    return this.makeRequest('/v1/report-imports/parsers');
  }

  static async reprocessReportImport(importId, parserId) {
    return this.makeRequest(`/v1/report-imports/${encodeURIComponent(importId)}/reprocess`, {
      method: 'POST',
      body: JSON.stringify(parserId ? { parserId } : {}),
    });
  }

  static async deleteReportImport(importId) {
    return this.makeRequest(`/v1/report-imports/${encodeURIComponent(importId)}`, { method: 'DELETE' });
  }

  static async getReportImportAnalytics(params = {}) {
    const qp = new URLSearchParams();
    Object.entries(params || {}).forEach(([k, v]) => {
      if (v === undefined || v === null || v === '') return;
      qp.set(k, v);
    });
    const qs = qp.toString();
    return this.makeRequest(`/v1/report-imports/analytics${qs ? `?${qs}` : ''}`);
  }

  static getReportArtifactUrl(storageKey) {
    if (!storageKey) return '';
    const qp = new URLSearchParams({ key: storageKey });
    return `${this.getEffectiveBaseUrl()}/v1/report-imports/artifacts/download?${qp.toString()}`;
  }
  static async exportRunJUnitXml(runId) {
    const auth = this.buildBearerAuthHeader();
    const headers = auth ? { Authorization: auth } : {};
    const res = await fetch(`${this.getEffectiveBaseUrl()}/testruns/${encodeURIComponent(runId)}/export/junit`, { headers });
    if (!res.ok) {
      let msg = '';
      try { msg = (await res.json())?.message || ''; } catch {}
      throw new Error(msg || `JUnit export failed (${res.status})`);
    }
    return res.text();
  }

  static async updateTestRunAssignments(runId, assignments = []) {
    return this.makeRequest(`/testruns/${runId}/assignments`, {
      method: 'PATCH',
      body: JSON.stringify({ assignments })
    });
  }

  static async getAssignees() { // minimal list for assignment UI
    return this.makeRequest('/users/assignees');
  }

  // Admin endpoints (superadmin only)
  static async getTenants() { return this.makeRequest('/admin/tenants'); }
  static async getUsersByTenant(tenantId) { return this.makeRequest(`/admin/tenants/${encodeURIComponent(tenantId)}/users`); }
  static async updateTenant(tenantId, data) {
    const id = String(tenantId || '');
    if (!id) throw new Error('Tenant id is required');
    return this.makeRequest(`/admin/tenants/${encodeURIComponent(id)}` , {
      method: 'PATCH',
      body: JSON.stringify(data)
    });
  }
  static async deleteTenant(tenantId) {
    const id = String(tenantId || '');
    if (!id) throw new Error('Tenant id is required');
    return this.makeRequest(`/admin/tenants/${encodeURIComponent(id)}`, {
      method: 'DELETE'
    });
  }
  static async deleteCurrentTenant() {
    return this.makeRequest('/tenants/current', {
      method: 'DELETE'
    });
  }

  // --------------------
  // Integrations (GitHub, GitLab, Bitbucket)
  // --------------------
  // TENANT EVENTS (Settings → Events + third-party pull)
  static async getEventsCatalog() {
    return this.makeRequest('/events/catalog');
  }

  static async listTenantEvents(params = {}) {
    const qp = new URLSearchParams();
    Object.entries(params || {}).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qp.set(k, v);
    });
    const qs = qp.toString();
    return this.makeRequest(`/events${qs ? `?${qs}` : ''}`);
  }

  // OUTBOUND WEBHOOKS (menu-bar agent / n8n)
  static async getWebhookEvents() {
    return this.makeRequest('/webhooks/events');
  }

  static async getWebhookEndpoints() {
    return this.makeRequest('/webhooks/endpoints');
  }

  static async createWebhookEndpoint(body = {}) {
    return this.makeRequest('/webhooks/endpoints', {
      method: 'POST',
      body: JSON.stringify(body || {}),
    });
  }

  static async updateWebhookEndpoint(id, body = {}) {
    return this.makeRequest(`/webhooks/endpoints/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(body || {}),
    });
  }

  static async deleteWebhookEndpoint(id) {
    return this.makeRequest(`/webhooks/endpoints/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }

  static async testWebhookEndpoint(id) {
    return this.makeRequest(`/webhooks/endpoints/${encodeURIComponent(id)}/test`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  static async getWebhookInbox(params = {}) {
    const qp = new URLSearchParams();
    Object.entries(params || {}).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qp.set(k, v);
    });
    const qs = qp.toString();
    return this.makeRequest(`/webhooks/inbox${qs ? `?${qs}` : ''}`);
  }

  static async ackWebhookInbox(deliveryIds = []) {
    return this.makeRequest('/webhooks/inbox/ack', {
      method: 'POST',
      body: JSON.stringify({ deliveryIds }),
    });
  }

  static async getIntegrations() { return this.makeRequest('/integrations'); }
  static async saveIntegration(provider, data) {
    return this.makeRequest(`/integrations/${provider}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }
  static async deleteIntegration(provider) {
    return this.makeRequest(`/integrations/${provider}`, { method: 'DELETE' });
  }
  static async testIntegration(provider) {
    return this.makeRequest(`/integrations/${provider}/test`, { method: 'POST' });
  }

  // Slack bot (Settings → Integrations → Slack bot; company-wide install)
  static async getSlackAppStatus() { return this.makeRequest('/slack/app/status'); }
  static async startSlackAppInstall() {
    return this.makeRequest('/slack/app/install', { method: 'POST', body: JSON.stringify({}) });
  }
  static async updateSlackAppSettings(body) {
    return this.makeRequest('/slack/app/settings', { method: 'PATCH', body: JSON.stringify(body || {}) });
  }
  static async sendSlackAppTest() {
    return this.makeRequest('/slack/app/test', { method: 'POST', body: JSON.stringify({}) });
  }
  static async disconnectSlackApp() { return this.makeRequest('/slack/app', { method: 'DELETE' }); }

  // --------------------
  // MCP connections (outbound for AI Companion)
  // --------------------
  static async getMcpPresets() {
    return this.makeRequest('/mcp/presets');
  }
  static async getMcpConnections() {
    return this.makeRequest('/mcp/connections');
  }
  static async createMcpConnection(data) {
    return this.makeRequest('/mcp/connections', {
      method: 'POST',
      body: JSON.stringify(data || {}),
    });
  }
  static async updateMcpConnection(id, data) {
    return this.makeRequest(`/mcp/connections/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(data || {}),
    });
  }

  /** Share or unshare all of the current user's MCP connections with the workspace. */
  static async shareAllMcpConnections(sharedWithWorkspace = true) {
    return this.makeRequest('/mcp/connections/share-all', {
      method: 'POST',
      body: JSON.stringify({ sharedWithWorkspace: !!sharedWithWorkspace }),
    });
  }
  static async deleteMcpConnection(id) {
    return this.makeRequest(`/mcp/connections/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }
  static async testMcpConnection(id) {
    return this.makeRequest(`/mcp/connections/${encodeURIComponent(id)}/test`, {
      method: 'POST',
    });
  }
  static async getMcpOauthStatus() {
    return this.makeRequest('/mcp/oauth/status');
  }

  static async saveSlackMcpApp({ clientId, clientSecret } = {}) {
    return this.makeRequest('/mcp/oauth/slack-app', {
      method: 'PUT',
      body: JSON.stringify({ clientId, clientSecret }),
    });
  }

  static async clearSlackMcpApp() {
    return this.makeRequest('/mcp/oauth/slack-app', { method: 'DELETE' });
  }
  static async startMcpOauth(data) {
    return this.makeRequest('/mcp/oauth/start', {
      method: 'POST',
      body: JSON.stringify(data || {}),
    });
  }

  // NEW: convenience helper for plan/seats updates (superadmin)
  static async updateTenantPlan(tenantId, { plan, seats } = {}) {
    const id = String(tenantId || '');
    if (!id) throw new Error('Tenant id is required');
    const payload = {};
    if (plan !== undefined) payload.plan = plan;
    if (seats !== undefined) payload.seats = seats;
    return this.updateTenant(id, payload);
  }

  // NEW: tenant billing/admin helpers
  static async getTenantSeatUsage(tenantId) {
    return this.makeRequest(`/admin/tenants/${encodeURIComponent(tenantId)}/seat-usage`);
  }

  static async setTenantTemporaryAccess(tenantId, { days = 7 } = {}) {
    return this.makeRequest(`/admin/tenants/${encodeURIComponent(tenantId)}/temporary-access`, {
      method: 'POST',
      body: JSON.stringify({ days })
    });
  }

  static async clearTenantTemporaryAccess(tenantId) {
    return this.makeRequest(`/admin/tenants/${encodeURIComponent(tenantId)}/temporary-access`, {
      method: 'DELETE'
    });
  }

  static async createTenantBillingPortalLink(tenantId, { returnUrl } = {}) {
    const qp = new URLSearchParams();
    if (returnUrl) qp.set('returnUrl', returnUrl);
    const qs = qp.toString();
    return this.makeRequest(`/admin/tenants/${encodeURIComponent(tenantId)}/billing-portal${qs ? ('?' + qs) : ''}`);
  }

  // --------------------
  // Superadmin: Observability / Ops
  // --------------------
  static async getOpsStatus() {
    return this.makeRequest('/admin/ops/status', { method: 'GET' });
  }

  static async getOpsMetrics(params = {}) {
    const qs = new URLSearchParams();
    if (params.top != null) qs.set('top', String(params.top));
    return this.makeRequest(`/admin/ops/metrics?${qs.toString()}`, { method: 'GET' });
  }

  /** Superadmin visitor analytics (first-party pageviews + geo). */
  static async getAdminVisitors(params = {}) {
    const qs = new URLSearchParams();
    if (params.days != null) qs.set('days', String(params.days));
    if (params.surface) qs.set('surface', String(params.surface));
    if (params.from) qs.set('from', String(params.from));
    if (params.to) qs.set('to', String(params.to));
    return this.makeRequest(`/admin/visitors?${qs.toString()}`, { method: 'GET' });
  }

  /** Superadmin live signup feed (audit auth.signup since watermark). */
  static async getAdminRecentSignups({ since, limit = 20 } = {}) {
    const qs = new URLSearchParams();
    if (since) qs.set('since', String(since));
    if (limit != null) qs.set('limit', String(limit));
    return this.makeRequest(`/admin/signups/recent?${qs.toString()}`, { method: 'GET' });
  }

  /** Product-wide feature flags (admin.testera.io). */
  static async getAdminFeatureFlags() {
    return this.makeRequest('/admin/feature-flags', { method: 'GET' });
  }

  static async updateAdminFeatureFlags(patch = {}) {
    return this.makeRequest('/admin/feature-flags', {
      method: 'PATCH',
      body: JSON.stringify(patch || {}),
    });
  }

  static async searchAdminAuditLogs(params = {}) {
    const qs = new URLSearchParams();
    const keys = ['q','method','methods','statusCode','statusMin','statusMax','action','entityType','entityId','userEmail','from','to','path','limit','skip','tenantId','tenantSlug'];
    keys.forEach(k => { if (params[k] != null && String(params[k]).length) qs.set(k, String(params[k])); });
    if (params.hideNoise) qs.set('hideNoise', '1');
    return this.makeRequest(`/admin/audit-logs?${qs.toString()}`, { method: 'GET' });
  }

  static async getAdminAuditDiff(id) {
    return this.makeRequest(`/admin/audit-logs/${id}/diff`, { method: 'GET' });
  }

  /** Mac notification agent pairing (device flow). */
  static async getAgentPairStatus(userCode) {
    const qs = new URLSearchParams({ user_code: String(userCode || '').trim() });
    return this.makeRequest(`/agent/pair/status?${qs}`, { method: 'GET' });
  }

  static async approveAgentPair(userCode) {
    return this.makeRequest('/agent/pair/approve', {
      method: 'POST',
      body: JSON.stringify({ userCode: String(userCode || '').trim() }),
    });
  }

  static async denyAgentPair(userCode) {
    return this.makeRequest('/agent/pair/deny', {
      method: 'POST',
      body: JSON.stringify({ userCode: String(userCode || '').trim() }),
    });
  }

  // --------------------
  // AI Provider Settings (Tenant & Admin)
  // --------------------
  static async getAiProviderSettings() {
    return this.makeRequest('/ai/provider-settings', { method: 'GET' });
  }

  static async updateAiProviderSettings(data) {
    return this.makeRequest('/ai/provider-settings', {
      method: 'PUT',
      body: JSON.stringify(data || {}),
    });
  }

  static async testAiProviderSettings(data) {
    return this.makeRequest('/ai/provider-settings/test', {
      method: 'POST',
      body: JSON.stringify(data || {}),
    });
  }

  static async getAdminAiProviderSettings() {
    return this.makeRequest('/admin/ai/provider-settings', { method: 'GET' });
  }

  static async updateAdminAiProviderSettings(data) {
    return this.makeRequest('/admin/ai/provider-settings', {
      method: 'PUT',
      body: JSON.stringify(data || {}),
    });
  }

  static async testAdminAiProviderSettings(data) {
    return this.makeRequest('/admin/ai/provider-settings/test', {
      method: 'POST',
      body: JSON.stringify(data || {}),
    });
  }
}

export default ApiService;

// Kick off offline sync (safe if called multiple times)
ApiService.initOfflineSync().catch(() => {});
